import { useCallback, useRef } from 'react';
import type { Annotation } from '../types';
import { HIT_BOX_PADDING } from '../constants';

/** Params for canvas interaction handlers - use `any` for complex types to minimize churn */
export interface UseCanvasInteractionHandlersParams {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  annotationsRef: React.MutableRefObject<Annotation[]>;
  lastMousePosRef: React.MutableRefObject<{ x: number; y: number } | null> | React.RefObject<{ x: number; y: number } | null>;
  lastWheelTimeRef: React.MutableRefObject<number>;
  dirtyWireRunAnnotationIdsRef: React.MutableRefObject<Set<string>>;
  lastPlacedAnnotationsRef: React.MutableRefObject<string[]>;

  getScreenToCanvasCoords: (clientX: number, clientY: number) => { x: number; y: number };
  findAnnotationAtPoint: (x: number, y: number) => { id: string; centerX: number; centerY: number; x?: number; y?: number; isWireRun?: boolean } | null;
  findPDFElementAtPoint: (x: number, y: number, elements: any[]) => any;
  addToHistory: (ann: Annotation[]) => void;
  persistAnnotations: (ann: Annotation[], changedIds?: string[]) => void | Promise<void>;
  persistCreatedAnnotations: (newItems: Annotation[]) => Promise<{ next: Annotation[]; changedComponentTypes: string[] }>;
  syncWireRunsToDatabase: (ids: string[], ann: Annotation[]) => void | Promise<void>;
  handleCancelSnapshot: () => void;
  handleExtractKey: () => Promise<void>;
  drawAnnotations: (withoutUI?: boolean) => void;
  rescaleAnnotation: (a: any, w: number, h: number) => any;
  normalizeWireRunGeometry: (a: any, points: { x: number; y: number }[], w: number, h: number) => any;
  constrainTo90Degrees: (anchor: { x: number; y: number }, target: { x: number; y: number }) => { x: number; y: number };
  findWireRunVertexIndexAtPoint: (a: any, x: number, y: number, tol?: number) => number | null;
  findWireRunSegmentAtPoint: (a: any, x: number, y: number, tol?: number) => { segmentIndex: number; insertPoint: { x: number; y: number }; t: number } | null;
  findWireRunEndpointAtPoint: (a: any, x: number, y: number, tol?: number) => 'start' | 'end' | null;
  updateConnectedPipes: (ann: Annotation[], ids: Set<string>) => Annotation[];
  openWireRunEditContextMenuAtPoint: (clientX: number, clientY: number, canvasX: number, canvasY: number) => boolean;
  renumberAnnotations: (ann: Annotation[]) => Annotation[];
  persistDeletedAnnotations: (ids: string[], types?: string[]) => void | Promise<void>;
  deletePersistedWireRunsForAnnotationIds: (ids: Set<string>) => void | Promise<void>;
  calculatePipeMeasurement: (distance: number, pxDx?: number, pxDy?: number) => { value: number; unit: string } | null;
  calculateCanvasPercentage: (x1: number, y1: number, x2: number, y2: number) => number;
  isPointInAnnotation: (a: any, x: number, y: number, pad?: number) => boolean;
  pointToLineDistance: (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => number;

  pageNumber: number;
  annotations: Annotation[];
  panX: number;
  panY: number;

  isElementDetectionMode: boolean;
  wireRunExtendMode: { annotationId: string; endpoint: 'start' | 'end' } | null;
  currentTool: string;
  hoveredAnnotation: string | null;
  isWireRunMode: boolean;
  selectedWireType: any;
  autocountMode: string | null;
  keyExtractionMode: boolean;
  lightingScheduleMode: boolean;
  isCalibrating: boolean;
  handDrawnEnabled: boolean;
  pipingStart: { annotationId: string; x: number; y: number } | null;
  pipingSnapTarget: { annotationId: string; x?: number; y?: number } | null;
  pipingPreview: { startX: number; startY: number; endX: number; endY: number } | null;
  showSnapshotActions: boolean;
  snapshotSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  wireRunContextMenu: any;
  wireRunEditContextMenu: any;
  activePlacementComponent: { name: string; symbol: string; color: string; symbolType?: string } | null | undefined;
  countingModeActive: boolean | undefined;
  selectedAnnotation: string | null;
  selectedAnnotations: Set<string>;
  highlightedComponentType: string | null;
  isHighlightAll: boolean;
  hasActiveFilter: boolean;
  viewAllMode: boolean;
  pdfVectorElements: any[];

  isPanning: boolean;
  panStart: { x: number; y: number; panX: number; panY: number } | null | undefined;
  resizeHandle: { annotationId: string; handle: string; startX: number; startY: number; origRect: any } | null;
  wireRunVertexDrag: { annotationId: string; pointIndex: number } | null;
  autocountSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  keyExtractionSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  lightingScheduleSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  calibrationLine: { startX: number; startY: number; endX: number; endY: number; percentage: number } | null;
  isDrawing: boolean;
  currentAnnotation: any;
  currentColor: string;
  thickness: number;
  dragStart: { x: number; y: number } | null;
  selectionRect: { startX: number; startY: number; endX: number; endY: number } | null;
  isSelecting: boolean;
  isCapturingSnapshot: boolean;
  wireRunExtendPreviewPoint: { x: number; y: number } | null | undefined;
  hoverCursor: string;
  isRightClickPanning: boolean;
  autoExtractKeyOnSnapshot?: boolean;
  isAddingNewOnlyScanMode?: boolean;
  isAddingNewOnly?: boolean;
  pendingAutocountComponent?: any;

  setHoveredAnnotation: (v: string | null) => void;
  setHoveredAnnotationPoint: (v: { x: number; y: number } | null) => void;
  setIsRightClickPanning: (v: boolean) => void;
  setIsPanning: (v: boolean) => void;
  setPanStart: (v: any) => void;
  setAutocountSelection: (v: any) => void;
  setKeyExtractionSelection: (v: any) => void;
  setLightingScheduleSelection: (v: any) => void;
  setCalibrationLine: (v: any) => void;
  setIsDrawing: (v: boolean) => void;
  setPipingStart: (v: any) => void;
  setPipingPreview: (v: any) => void;
  setPipingSnapTarget: (v: any) => void;
  setSnapshotSelection: (v: any) => void;
  setWireRunContextMenu: (v: any) => void;
  setWireRunEditContextMenu: (v: any) => void;
  setCurrentAnnotation: (v: any) => void;
  setResizeHandle: (v: any) => void;
  setWireRunExtendMode: (v: any) => void;
  setWireRunExtendPreviewPoint: (v: any) => void;
  setSelectedAnnotation: (v: string | null) => void;
  setSelectedAnnotations: (v: Set<string>) => void;
  setWireRunVertexDrag: (v: any) => void;
  setSelectionRect: (v: any) => void;
  setIsSelecting: (v: boolean) => void;
  setDragStart: (v: any) => void;
  setIsCapturingSnapshot: (v: boolean) => void;
  setShowSnapshotActions: (v: boolean) => void;
  setHoverCursor: (v: string) => void;
  setHoveredPDFElement: (v: any) => void;
  setShowCalibrationModal: (v: boolean) => void;
  setReferenceAreaImage: (v: string | null) => void;
  setEditingQueuedName: (v: string) => void;
  setAutocountMode: (v: any) => void;
  setShowNewComponentModal: (v: boolean) => void;
  setShowAutocountConfirmModal: (v: boolean) => void;
  setShowKeyExtractionConfirmModal: (v: boolean) => void;
  setKeyExtractionSnapshot: (v: string | null) => void;
  setLightingScheduleSnapshot: (v: string | null) => void;
  setShowLightingScheduleConfirmModal: (v: boolean) => void;
  setAnnotations: (v: Annotation[] | ((prev: Annotation[]) => Annotation[])) => void;
  setPanX: (v: number | ((prev: number) => number)) => void;
  setPanY: (v: number | ((prev: number) => number)) => void;

  onSwitchToWiresTab?: () => void;
  onPlacementComplete?: () => void;
}

export function useCanvasInteractionHandlers(p: UseCanvasInteractionHandlersParams) {
  const HOVER_UPDATE_MIN_DISTANCE_PX = 12;
  const HOVER_SUPPRESS_AFTER_ZOOM_MS = 140;
  const lastHoverPointRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (p.isElementDetectionMode) return;

      const canvas = p.canvasRef.current;
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      const isOnClickCanvas =
        e.clientX >= canvasRect.left &&
        e.clientX <= canvasRect.right &&
        e.clientY >= canvasRect.top &&
        e.clientY <= canvasRect.bottom;

      const { x, y } = p.getScreenToCanvasCoords(e.clientX, e.clientY);

      if (p.wireRunExtendMode && e.button === 2) {
        e.preventDefault();
        return;
      }

      if (p.currentTool !== 'select' && p.currentTool !== 'drag' && p.hoveredAnnotation) {
        p.setHoveredAnnotation(null);
        p.setHoveredAnnotationPoint(null);
        lastHoverPointRef.current = null;
      }

      if (
        (e.button === 2 || e.button === 1 || p.currentTool === 'drag' || (!isOnClickCanvas && e.button === 0)) &&
        !p.isWireRunMode &&
        !p.wireRunExtendMode
      ) {
        if (e.button === 2) {
          const hitAnnotation = p.findAnnotationAtPoint(x, y);
          if (hitAnnotation) return;
        }
        e.preventDefault();
        if (e.button === 2) p.setIsRightClickPanning(true);
        p.setIsPanning(true);
        p.setPanStart({
          x: e.clientX,
          y: e.clientY,
          panX: p.panX,
          panY: p.panY,
        });
        return;
      }

      if (!isOnClickCanvas) return;

      if (p.autocountMode === 'area-selection') {
        p.setAutocountSelection({ startX: x, startY: y, endX: x, endY: y });
        return;
      }

      if (p.keyExtractionMode) {
        p.setKeyExtractionSelection({ startX: x, startY: y, endX: x, endY: y });
        return;
      }

      if (p.lightingScheduleMode) {
        p.setLightingScheduleSelection({ startX: x, startY: y, endX: x, endY: y });
        return;
      }

      console.log('🎯 Click at canvas coordinates:', { x: Math.round(x), y: Math.round(y) });
      console.log('🔍 Mouse down - isCalibrating:', p.isCalibrating, 'handDrawnEnabled:', p.handDrawnEnabled);

      if (p.isCalibrating) {
        console.log('📏 Calibration: Starting line at', { x: Math.round(x), y: Math.round(y) });
        p.setCalibrationLine({
          startX: x,
          startY: y,
          endX: x,
          endY: y,
          percentage: 0,
        });
        p.setIsDrawing(true);
        console.log('✅ Calibration line started, now drag to set endpoint');
        return;
      }

      if (p.currentTool === 'piping') {
        if (!p.pipingStart) {
          const annotation = p.findAnnotationAtPoint(x, y);
          if (annotation) {
            p.setPipingStart({
              annotationId: annotation.id,
              x: annotation.centerX,
              y: annotation.centerY,
            });
            p.setPipingPreview({
              startX: annotation.centerX,
              startY: annotation.centerY,
              endX: annotation.centerX,
              endY: annotation.centerY,
            });
            p.setPipingSnapTarget(null);
            console.log('🔧 Started piping from annotation:', annotation.id);
          }
        } else {
          const targetAnnotation = p.pipingSnapTarget
            ? {
                id: p.pipingSnapTarget.annotationId,
                centerX: p.pipingSnapTarget.x ?? 0,
                centerY: p.pipingSnapTarget.y ?? 0,
              }
            : p.findAnnotationAtPoint(x, y);

          if (targetAnnotation && targetAnnotation.id !== p.pipingStart.annotationId) {
            const dx = targetAnnotation.centerX - p.pipingStart.x;
            const dy = targetAnnotation.centerY - p.pipingStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            const measurement = p.calculatePipeMeasurement(distance);

            const newPipe: Annotation = {
              id: `pipe-${Date.now()}`,
              type: 'piping',
              page: p.pageNumber,
              startX: p.pipingStart.x,
              startY: p.pipingStart.y,
              endX: targetAnnotation.centerX,
              endY: targetAnnotation.centerY,
              color: 'rgb(220, 38, 38)',
              thickness: 4,
              timestamp: new Date().toISOString(),
              startAnnotationId: p.pipingStart.annotationId,
              endAnnotationId: targetAnnotation.id,
              measurement: measurement?.value,
              measurementUnit: measurement?.unit,
            };

            const newAnnotations = [...p.annotationsRef.current, newPipe];
            p.annotationsRef.current = newAnnotations;
            p.setAnnotations(newAnnotations);
            p.addToHistory(newAnnotations);

            p.setPipingStart(null);
            p.setPipingPreview(null);
            p.setPipingSnapTarget(null);

            console.log('🔧 Created pipe:', measurement ? `${measurement.value}${measurement.unit}` : 'no calibration');
          }
        }
        return;
      }

      if (p.showSnapshotActions && p.snapshotSelection) {
        const isInsideSelection =
          x >= Math.min(p.snapshotSelection.startX, p.snapshotSelection.endX) &&
          x <= Math.max(p.snapshotSelection.startX, p.snapshotSelection.endX) &&
          y >= Math.min(p.snapshotSelection.startY, p.snapshotSelection.endY) &&
          y <= Math.max(p.snapshotSelection.startY, p.snapshotSelection.endY);

        if (!isInsideSelection) {
          p.handleCancelSnapshot();
          return;
        }
      }

      if (p.currentTool !== 'snapshot' && p.snapshotSelection) {
        p.setSnapshotSelection(null);
      }

      if (p.wireRunContextMenu) {
        p.setWireRunContextMenu(null);
      }
      if (p.wireRunEditContextMenu) {
        p.setWireRunEditContextMenu(null);
      }

      console.log('🔍 Placement check:', {
        activePlacementComponent: p.activePlacementComponent?.name,
        currentTool: p.currentTool,
        countingModeActive: p.countingModeActive,
      });
      if (p.activePlacementComponent && p.currentTool === 'select' && p.countingModeActive) {
        console.log('✅ Starting component rectangle drawing!');

        p.setIsDrawing(true);
        const placementComponentType =
          p.activePlacementComponent.symbolType === 'Lighting'
            ? p.activePlacementComponent.symbol || p.activePlacementComponent.name
            : p.activePlacementComponent.name;
        p.setCurrentAnnotation({
          id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'rectangle',
          startX: x,
          startY: y,
          width: 0,
          height: 0,
          color: p.activePlacementComponent.color,
          thickness: 3,
          timestamp: new Date().toISOString(),
          page: p.pageNumber,
          component_type: placementComponentType,
        });

        return;
      }

      if (p.currentTool === 'select') {
        const cw = canvas?.width || 16000;
        const ch = canvas?.height || 10666;

        const handleSize = 10;
        const selectedRectangles = p.annotations
          .filter(
            (a) =>
              a.page === p.pageNumber &&
              a.type === 'rectangle' &&
              (p.selectedAnnotations.has(a.id) || a.id === p.selectedAnnotation) &&
              (p.isHighlightAll || (p.hasActiveFilter && a.component_type === p.highlightedComponentType))
          )
          .map((a) => p.rescaleAnnotation(a, cw, ch));

        for (const rect of selectedRectangles) {
          const handles = [
            { key: 'nw' as const, x: rect.startX, y: rect.startY },
            { key: 'ne' as const, x: rect.startX + (rect.width || 0), y: rect.startY },
            { key: 'sw' as const, x: rect.startX, y: rect.startY + (rect.height || 0) },
            { key: 'se' as const, x: rect.startX + (rect.width || 0), y: rect.startY + (rect.height || 0) },
          ];

          for (const handle of handles) {
            if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
              console.log('🔲 Started resizing rectangle from handle:', handle.key);
              p.setResizeHandle({
                annotationId: rect.id,
                handle: handle.key,
                startX: x,
                startY: y,
                origRect: {
                  startX: rect.startX,
                  startY: rect.startY,
                  width: rect.width || 0,
                  height: rect.height || 0,
                },
              });
              return;
            }
          }
        }

        if (p.wireRunExtendMode && e.button === 0) {
          const targetWireRun = p.annotations
            .filter(
              (a) =>
                a.page === p.pageNumber &&
                a.type === 'wire-run' &&
                a.id === p.wireRunExtendMode!.annotationId
            )
            .map((a) => p.rescaleAnnotation(a, cw, ch))[0];

          if (!targetWireRun?.points || targetWireRun.points.length < 2) {
            p.setWireRunExtendMode(null);
            p.setWireRunExtendPreviewPoint(null);
            return;
          }

          const endpointIndex = p.wireRunExtendMode.endpoint === 'start' ? 0 : targetWireRun.points.length - 1;
          const endpoint = targetWireRun.points[endpointIndex];
          const nextPoint = e.shiftKey ? p.constrainTo90Degrees(endpoint, { x, y }) : { x, y };
          if (Math.hypot(x - endpoint.x, y - endpoint.y) <= 6) {
            p.setWireRunExtendMode(null);
            p.setWireRunExtendPreviewPoint(null);
            return;
          }

          const nextPoints =
            p.wireRunExtendMode.endpoint === 'start'
              ? [nextPoint, ...targetWireRun.points]
              : [...targetWireRun.points, nextPoint];
          const currentAnnotations = p.annotationsRef.current;
          const nextAnnotations = currentAnnotations.map((annotation) => {
            if (annotation.id !== targetWireRun.id || annotation.type !== 'wire-run') return annotation;
            return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
          });
          p.annotationsRef.current = nextAnnotations;
          p.setAnnotations(nextAnnotations);
          p.addToHistory(nextAnnotations);
          void p.persistAnnotations(nextAnnotations, [targetWireRun.id]);
          void p.syncWireRunsToDatabase([targetWireRun.id], nextAnnotations);

          p.setSelectedAnnotation(targetWireRun.id);
          p.setSelectedAnnotations(new Set([targetWireRun.id]));
          p.setWireRunExtendPreviewPoint(null);
          return;
        }

        const selectedWireRun = p.annotations
          .filter(
            (a) =>
              a.page === p.pageNumber &&
              a.type === 'wire-run' &&
              (p.selectedAnnotations.has(a.id) || a.id === p.selectedAnnotation)
          )
          .map((a) => p.rescaleAnnotation(a, cw, ch))[0];

        if (selectedWireRun?.points && selectedWireRun.points.length >= 2) {
          const vertexIndex = p.findWireRunVertexIndexAtPoint(selectedWireRun, x, y, 14);
          const segmentHit =
            vertexIndex === null ? p.findWireRunSegmentAtPoint(selectedWireRun, x, y, 12) : null;
          const endpointHit = p.findWireRunEndpointAtPoint(selectedWireRun, x, y, 14);

          if (endpointHit && e.shiftKey) {
            p.setWireRunExtendMode({ annotationId: selectedWireRun.id, endpoint: endpointHit });
            p.setWireRunExtendPreviewPoint(null);
            p.setSelectedAnnotation(selectedWireRun.id);
            p.setSelectedAnnotations(new Set([selectedWireRun.id]));
            return;
          }

          if (vertexIndex !== null && (e.altKey || e.metaKey)) {
            if (
              selectedWireRun.points.length > 2 &&
              vertexIndex > 0 &&
              vertexIndex < selectedWireRun.points.length - 1
            ) {
              const nextPoints = selectedWireRun.points.filter((_: { x: number; y: number }, index: number) => index !== vertexIndex);
              const currentAnnotations = p.annotationsRef.current;
              const nextAnnotations = currentAnnotations.map((annotation) => {
                if (annotation.id !== selectedWireRun.id || annotation.type !== 'wire-run') return annotation;
                return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
              });
              p.annotationsRef.current = nextAnnotations;
              p.setAnnotations(nextAnnotations);
              p.addToHistory(nextAnnotations);
              void p.persistAnnotations(nextAnnotations, [selectedWireRun.id]);
              void p.syncWireRunsToDatabase([selectedWireRun.id], nextAnnotations);
            }
            return;
          }

          if (vertexIndex !== null && e.detail >= 2) {
            if (
              selectedWireRun.points.length > 2 &&
              vertexIndex > 0 &&
              vertexIndex < selectedWireRun.points.length - 1
            ) {
              const nextPoints = selectedWireRun.points.filter((_: { x: number; y: number }, index: number) => index !== vertexIndex);
              const currentAnnotations = p.annotationsRef.current;
              const nextAnnotations = currentAnnotations.map((annotation) => {
                if (annotation.id !== selectedWireRun.id || annotation.type !== 'wire-run') return annotation;
                return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
              });
              p.annotationsRef.current = nextAnnotations;
              p.setAnnotations(nextAnnotations);
              p.addToHistory(nextAnnotations);
              void p.persistAnnotations(nextAnnotations, [selectedWireRun.id]);
              void p.syncWireRunsToDatabase([selectedWireRun.id], nextAnnotations);
            }
            return;
          }

          if (vertexIndex !== null) {
            p.setWireRunVertexDrag({
              annotationId: selectedWireRun.id,
              pointIndex: vertexIndex,
            });
            p.setSelectedAnnotation(selectedWireRun.id);
            p.setSelectedAnnotations(new Set([selectedWireRun.id]));
            p.setDragStart({ x, y });
            return;
          }

          if (segmentHit && e.detail >= 2) {
            const nextPoints = [...selectedWireRun.points];
            nextPoints.splice(segmentHit.segmentIndex + 1, 0, segmentHit.insertPoint);
            const currentAnnotations = p.annotationsRef.current;
            const nextAnnotations = currentAnnotations.map((annotation) => {
              if (annotation.id !== selectedWireRun.id || annotation.type !== 'wire-run') return annotation;
              return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
            });
            p.annotationsRef.current = nextAnnotations;
            p.setAnnotations(nextAnnotations);
            p.addToHistory(nextAnnotations);
            void p.persistAnnotations(nextAnnotations, [selectedWireRun.id]);
            void p.syncWireRunsToDatabase([selectedWireRun.id], nextAnnotations);
            return;
          }

          if (segmentHit && (e.altKey || e.metaKey) && selectedWireRun.points.length > 2) {
            const preferredIndex = segmentHit.t < 0.5 ? segmentHit.segmentIndex : segmentHit.segmentIndex + 1;
            const removableIndex = Math.max(
              1,
              Math.min(selectedWireRun.points.length - 2, preferredIndex)
            );
            const nextPoints = selectedWireRun.points.filter((_: { x: number; y: number }, index: number) => index !== removableIndex);
            const currentAnnotations = p.annotationsRef.current;
            const nextAnnotations = currentAnnotations.map((annotation) => {
              if (annotation.id !== selectedWireRun.id || annotation.type !== 'wire-run') return annotation;
              return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
            });
            p.annotationsRef.current = nextAnnotations;
            p.setAnnotations(nextAnnotations);
            p.addToHistory(nextAnnotations);
            void p.persistAnnotations(nextAnnotations, [selectedWireRun.id]);
            void p.syncWireRunsToDatabase([selectedWireRun.id], nextAnnotations);
            return;
          }
        }

        const hitCw = p.canvasRef.current?.width || 16000;
        const hitCh = p.canvasRef.current?.height || 10666;
        const clickedAnnotation = p.annotations
          .filter((a) => a.page === p.pageNumber)
          .filter((a) => {
            if (a.type === 'wire-run') return true;
            if (!p.highlightedComponentType) return false;
            if (p.isHighlightAll) return true;
            if (a.type === 'piping') {
              const startAnn = p.annotations.find((ann) => ann.id === a.startAnnotationId);
              const endAnn = p.annotations.find((ann) => ann.id === a.endAnnotationId);
              return (
                startAnn?.component_type === p.highlightedComponentType ||
                endAnn?.component_type === p.highlightedComponentType
              );
            }
            return a.component_type === p.highlightedComponentType;
          })
          .map((a) => p.rescaleAnnotation(a, hitCw, hitCh))
          .find((a) => {
            if (a.type === 'rectangle' || a.type === 'circle') {
              return p.isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
            }
            if (a.type === 'line' || a.type === 'piping') {
              const dist = p.pointToLineDistance(
                x,
                y,
                a.startX,
                a.startY,
                a.endX || a.startX,
                a.endY || a.startY
              );
              return dist < HIT_BOX_PADDING;
            }
            if (a.type === 'pen' && a.points) {
              for (let i = 1; i < a.points.length; i++) {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.points[i - 1].x,
                  a.points[i - 1].y,
                  a.points[i].x,
                  a.points[i].y
                );
                if (dist < HIT_BOX_PADDING) return true;
              }
            }
            if (a.type === 'wire-run' && a.points && a.points.length >= 2) {
              for (let i = 0; i < a.points.length - 1; i++) {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.points[i].x,
                  a.points[i].y,
                  a.points[i + 1].x,
                  a.points[i + 1].y
                );
                if (dist < HIT_BOX_PADDING) return true;
              }
            }
            return false;
          });

        if (clickedAnnotation) {
          if (clickedAnnotation.type !== 'wire-run' && p.wireRunExtendMode) {
            p.setWireRunExtendMode(null);
            p.setWireRunExtendPreviewPoint(null);
          }
          if (clickedAnnotation.type === 'wire-run' && p.onSwitchToWiresTab) {
            console.log('🔌 Wire run clicked, switching to Wires tab');
            p.onSwitchToWiresTab();
          }

          if (clickedAnnotation.type === 'wire-run' && e.shiftKey) {
            const endpoint = p.findWireRunEndpointAtPoint(clickedAnnotation, x, y, 14);
            if (endpoint) {
              p.setSelectedAnnotation(clickedAnnotation.id);
              p.setSelectedAnnotations(new Set([clickedAnnotation.id]));
              p.setWireRunExtendMode({ annotationId: clickedAnnotation.id, endpoint });
              p.setWireRunExtendPreviewPoint(null);
              p.setDragStart(null);
              return;
            }
          }

          if (p.selectedAnnotations.has(clickedAnnotation.id) && p.selectedAnnotations.size > 0) {
            p.setDragStart({ x, y });
          } else {
            p.setSelectedAnnotation(clickedAnnotation.id);
            p.setSelectedAnnotations(new Set([clickedAnnotation.id]));
            p.setDragStart({ x, y });
          }
        } else {
          if (p.wireRunExtendMode) {
            p.setWireRunExtendMode(null);
            p.setWireRunExtendPreviewPoint(null);
          }
          p.setIsSelecting(true);
          p.setSelectionRect({ startX: x, startY: y, endX: x, endY: y });
          p.setSelectedAnnotations(new Set());
          p.setSelectedAnnotation(null);
        }
      } else if (p.currentTool === 'drag' && e.button === 0) {
        const clickedAnnotation = p.annotations
          .filter((a) => a.page === p.pageNumber)
          .filter((a) => {
            if (a.type === 'wire-run') return true;
            if (!p.highlightedComponentType) return false;
            if (p.isHighlightAll) return true;
            return a.component_type === p.highlightedComponentType;
          })
          .find((a) => {
            if (a.type === 'piping') return false;
            if (a.type === 'rectangle' || a.type === 'circle') {
              return p.isPointInAnnotation(a, x, y, 0);
            }
            if (a.type === 'line') {
              const dist = p.pointToLineDistance(
                x,
                y,
                a.startX,
                a.startY,
                a.endX || a.startX,
                a.endY || a.startY
              );
              return dist < 5;
            }
            if (a.type === 'pen' && a.points) {
              for (let i = 1; i < a.points.length; i++) {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.points[i - 1].x,
                  a.points[i - 1].y,
                  a.points[i].x,
                  a.points[i].y
                );
                if (dist < 5) return true;
              }
            }
            if (a.type === 'wire-run' && a.points) {
              for (let i = 1; i < a.points.length; i++) {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.points[i - 1].x,
                  a.points[i - 1].y,
                  a.points[i].x,
                  a.points[i].y
                );
                if (dist < 8) return true;
              }
            }
            return false;
          });

        if (clickedAnnotation) {
          p.setSelectedAnnotation(clickedAnnotation.id);
          p.setDragStart({ x, y });
        } else {
          p.setSelectedAnnotation(null);
          p.setIsPanning(true);
          p.setPanStart({
            x: e.clientX,
            y: e.clientY,
            panX: p.panX,
            panY: p.panY,
          });
        }
      } else if (p.currentTool === 'snapshot') {
        p.setIsCapturingSnapshot(true);
        p.setSnapshotSelection({
          startX: x,
          startY: y,
          endX: x,
          endY: y,
        });
      } else {
        p.setIsDrawing(true);
        const newAnnotation: Partial<Annotation> = {
          id: `annotation-${Date.now()}`,
          type: p.currentTool as any,
          page: p.pageNumber,
          startX: x,
          startY: y,
          color: p.currentColor,
          thickness: p.thickness,
          timestamp: new Date().toISOString(),
        };

        if (p.currentTool === 'pen') {
          newAnnotation.points = [{ x, y }];
        }

        p.setCurrentAnnotation(newAnnotation);
      }
    },
    [
      p.canvasRef,
      p.annotationsRef,
      p.getScreenToCanvasCoords,
      p.findAnnotationAtPoint,
      p.isElementDetectionMode,
      p.wireRunExtendMode,
      p.currentTool,
      p.hoveredAnnotation,
      p.isWireRunMode,
      p.panX,
      p.panY,
      p.autocountMode,
      p.keyExtractionMode,
      p.lightingScheduleMode,
      p.isCalibrating,
      p.handDrawnEnabled,
      p.pipingStart,
      p.pipingSnapTarget,
      p.pipingPreview,
      p.showSnapshotActions,
      p.snapshotSelection,
      p.wireRunContextMenu,
      p.wireRunEditContextMenu,
      p.activePlacementComponent,
      p.countingModeActive,
      p.annotations,
      p.pageNumber,
      p.selectedAnnotation,
      p.selectedAnnotations,
      p.highlightedComponentType,
      p.isHighlightAll,
      p.hasActiveFilter,
      p.currentColor,
      p.thickness,
      p,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      p.lastMousePosRef.current = { x: e.clientX, y: e.clientY };

      if (p.isPanning && p.panStart) {
        const dx = e.clientX - p.panStart.x;
        const dy = e.clientY - p.panStart.y;
        p.setPanX(p.panStart.panX + dx);
        p.setPanY(p.panStart.panY + dy);
        return;
      }

      const canvas = p.canvasRef.current;
      if (!canvas) return;

      const { x, y } = p.getScreenToCanvasCoords(e.clientX, e.clientY);

      if (p.resizeHandle) {
        const { annotationId, handle, origRect } = p.resizeHandle;
        const dx = x - p.resizeHandle.startX;
        const dy = y - p.resizeHandle.startY;

        p.setAnnotations((prev: Annotation[]) =>
          prev.map((ann) => {
            if (ann.id !== annotationId) return ann;

            let newStartX = origRect.startX;
            let newStartY = origRect.startY;
            let newWidth = origRect.width;
            let newHeight = origRect.height;

            switch (handle) {
              case 'nw':
                newStartX = origRect.startX + dx;
                newStartY = origRect.startY + dy;
                newWidth = origRect.width - dx;
                newHeight = origRect.height - dy;
                break;
              case 'ne':
                newStartY = origRect.startY + dy;
                newWidth = origRect.width + dx;
                newHeight = origRect.height - dy;
                break;
              case 'sw':
                newStartX = origRect.startX + dx;
                newWidth = origRect.width - dx;
                newHeight = origRect.height + dy;
                break;
              case 'se':
                newWidth = origRect.width + dx;
                newHeight = origRect.height + dy;
                break;
            }

            if (newWidth < 0) {
              if (handle === 'nw' || handle === 'sw') {
                newStartX = origRect.startX + origRect.width - 20;
              }
              newWidth = 0;
            }
            if (newHeight < 0) {
              if (handle === 'nw' || handle === 'ne') {
                newStartY = origRect.startY + origRect.height - 20;
              }
              newHeight = 0;
            }

            return {
              ...ann,
              startX: newStartX,
              startY: newStartY,
              width: newWidth,
              height: newHeight,
              relativeStartX: undefined,
              relativeStartY: undefined,
              relativeWidth: undefined,
              relativeHeight: undefined,
            };
          })
        );
        return;
      }

      if (p.currentTool === 'select' && p.wireRunVertexDrag) {
        const { annotationId, pointIndex } = p.wireRunVertexDrag;
        p.setAnnotations((prev: Annotation[]) => {
          const updated = prev.map((annotation) => {
            if (
              annotation.id !== annotationId ||
              annotation.type !== 'wire-run' ||
              !annotation.points ||
              annotation.points.length < 2
            ) {
              return annotation;
            }

            const nextPoints = annotation.points.map((point, index) =>
              index === pointIndex ? { x, y } : point
            );
            p.dirtyWireRunAnnotationIdsRef.current.add(annotationId);
            return p.normalizeWireRunGeometry(annotation, nextPoints, canvas.width, canvas.height);
          });
          p.annotationsRef.current = updated;
          return updated;
        });
        return;
      }

      if (p.autocountMode === 'area-selection' && p.autocountSelection) {
        p.setAutocountSelection({
          ...p.autocountSelection,
          endX: x,
          endY: y,
        });
        return;
      }

      if (p.keyExtractionMode && p.keyExtractionSelection) {
        p.setKeyExtractionSelection({
          ...p.keyExtractionSelection,
          endX: x,
          endY: y,
        });
        return;
      }

      if (p.lightingScheduleMode && p.lightingScheduleSelection) {
        p.setLightingScheduleSelection({
          ...p.lightingScheduleSelection,
          endX: x,
          endY: y,
        });
        return;
      }

      if (p.currentTool === 'select' && !p.resizeHandle) {
        const handleSize = 12;
        let foundCursor = 'default';

        const selectedRectangles = p.annotations.filter(
          (a) =>
            a.page === p.pageNumber &&
            a.type === 'rectangle' &&
            (p.selectedAnnotations.has(a.id) || a.id === p.selectedAnnotation) &&
            (p.isHighlightAll || (p.hasActiveFilter && a.component_type === p.highlightedComponentType))
        );

        for (const rect of selectedRectangles) {
          const handles = [
            { key: 'nw', x: rect.startX, y: rect.startY, cursor: 'nw-resize' },
            { key: 'ne', x: rect.startX + (rect.width || 0), y: rect.startY, cursor: 'ne-resize' },
            { key: 'sw', x: rect.startX, y: rect.startY + (rect.height || 0), cursor: 'sw-resize' },
            { key: 'se', x: rect.startX + (rect.width || 0), y: rect.startY + (rect.height || 0), cursor: 'se-resize' },
          ];

          for (const handle of handles) {
            if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
              foundCursor = handle.cursor;
              break;
            }
          }
          if (foundCursor !== 'default') break;
        }

        if (foundCursor !== p.hoverCursor) {
          p.setHoverCursor(foundCursor);
        }
      }

      if (p.currentTool === 'select' && p.wireRunExtendMode) {
        const targetWireRun = p.annotations
          .filter(
            (a) =>
              a.page === p.pageNumber &&
              a.type === 'wire-run' &&
              a.id === p.wireRunExtendMode!.annotationId
          )
          .map((a) => p.rescaleAnnotation(a, canvas.width || 16000, canvas.height || 10666))[0];

        if (targetWireRun?.points && targetWireRun.points.length >= 2) {
          const endpointIndex = p.wireRunExtendMode.endpoint === 'start' ? 0 : targetWireRun.points.length - 1;
          const endpoint = targetWireRun.points[endpointIndex];
          const constrainedPoint = e.shiftKey ? p.constrainTo90Degrees(endpoint, { x, y }) : { x, y };
          p.setWireRunExtendPreviewPoint(constrainedPoint);
        } else if (p.wireRunExtendPreviewPoint) {
          p.setWireRunExtendPreviewPoint(null);
        }
        return;
      } else if (p.wireRunExtendPreviewPoint) {
        p.setWireRunExtendPreviewPoint(null);
      }

      if (p.isElementDetectionMode) {
        const hoveredElement = p.findPDFElementAtPoint(x, y, p.pdfVectorElements);
        p.setHoveredPDFElement(hoveredElement);
        return;
      }

      if (p.currentTool === 'piping' && p.pipingStart && p.pipingPreview) {
        const SNAP_DISTANCE = 100;

        let snapTarget: { annotationId: string; x: number; y: number } | null = null;
        let closestDistance = SNAP_DISTANCE;

        const pageAnns = p.annotations.filter((a) => {
          if (a.page !== p.pageNumber) return false;
          if (!p.highlightedComponentType) return false;
          if (p.isHighlightAll) return true;
          return a.component_type === p.highlightedComponentType;
        });

        for (const ann of pageAnns) {
          if (ann.id === p.pipingStart.annotationId) continue;
          if (ann.type !== 'circle' && ann.type !== 'rectangle') continue;

          let centerX: number, centerY: number;

          if (ann.type === 'circle') {
            if (ann.x !== undefined && ann.y !== undefined) {
              centerX = ann.x;
              centerY = ann.y;
            } else {
              centerX = ann.startX + (ann.width || 0) / 2;
              centerY = ann.startY + (ann.height || 0) / 2;
            }
          } else if (ann.type === 'rectangle') {
            centerX = ann.startX + (ann.width || 0) / 2;
            centerY = ann.startY + (ann.height || 0) / 2;
          } else {
            continue;
          }

          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < closestDistance) {
            closestDistance = distance;
            snapTarget = { annotationId: ann.id, x: centerX, y: centerY };
          }
        }

        p.setPipingSnapTarget(snapTarget);
        p.setPipingPreview({
          ...p.pipingPreview,
          endX: snapTarget ? snapTarget.x : x,
          endY: snapTarget ? snapTarget.y : y,
        });
        return;
      }

      if (p.isCalibrating && p.isDrawing && p.calibrationLine) {
        let endX = x;
        let endY = y;

        if (e.metaKey || e.ctrlKey) {
          const dx = x - p.calibrationLine.startX;
          const dy = y - p.calibrationLine.startY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          const snapAngle = Math.round(angle / 15) * 15;
          const radians = snapAngle * (Math.PI / 180);
          endX = p.calibrationLine.startX + distance * Math.cos(radians);
          endY = p.calibrationLine.startY + distance * Math.sin(radians);
        }

        const percentage = p.calculateCanvasPercentage(
          p.calibrationLine.startX,
          p.calibrationLine.startY,
          endX,
          endY
        );

        p.setCalibrationLine({
          ...p.calibrationLine,
          endX,
          endY,
          percentage,
        });
        return;
      }

      if (p.currentTool === 'select' && p.isSelecting && p.selectionRect) {
        p.setSelectionRect({ ...p.selectionRect, endX: x, endY: y });

        const minX = Math.min(p.selectionRect.startX, x);
        const maxX = Math.max(p.selectionRect.startX, x);
        const minY = Math.min(p.selectionRect.startY, y);
        const maxY = Math.max(p.selectionRect.startY, y);

        const selected = new Set<string>();
        p.annotations
          .filter((a) => a.page === p.pageNumber)
          .filter((a) => {
            if (a.type === 'wire-run') return true;
            if (!p.highlightedComponentType) return false;
            if (p.isHighlightAll) return true;
            if (a.type === 'piping') {
              const startAnn = p.annotations.find((ann) => ann.id === a.startAnnotationId);
              const endAnn = p.annotations.find((ann) => ann.id === a.endAnnotationId);
              return (
                startAnn?.component_type === p.highlightedComponentType ||
                endAnn?.component_type === p.highlightedComponentType
              );
            }
            return a.component_type === p.highlightedComponentType;
          })
          .forEach((a) => {
            let isInside = false;

            if (a.type === 'rectangle' || a.type === 'circle') {
              if (a.radius) {
                const centerX = a.x || a.startX;
                const centerY = a.y || a.startY;
                const closestX = Math.max(minX, Math.min(maxX, centerX));
                const closestY = Math.max(minY, Math.min(maxY, centerY));
                const distance = Math.sqrt(
                  Math.pow(closestX - centerX, 2) + Math.pow(closestY - centerY, 2)
                );
                isInside = distance <= a.radius;
              } else {
                const aMinX = Math.min(a.startX, a.startX + (a.width || 0));
                const aMaxX = Math.max(a.startX, a.startX + (a.width || 0));
                const aMinY = Math.min(a.startY, a.startY + (a.height || 0));
                const aMaxY = Math.max(a.startY, a.startY + (a.height || 0));
                isInside = !(aMaxX < minX || aMinX > maxX || aMaxY < minY || aMinY > maxY);
              }
            } else if (a.type === 'line') {
              isInside =
                (a.startX >= minX && a.startX <= maxX && a.startY >= minY && a.startY <= maxY) ||
                ((a.endX || 0) >= minX &&
                  (a.endX || 0) <= maxX &&
                  (a.endY || 0) >= minY &&
                  (a.endY || 0) <= maxY);
            } else if (a.type === 'pen' && a.points) {
              isInside = a.points.some(
                (pt: { x: number; y: number }) =>
                  pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY
              );
            } else if (a.type === 'wire-run' && a.points) {
              isInside = a.points.some(
                (pt: { x: number; y: number }) =>
                  pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY
              );
            }

            if (isInside) {
              selected.add(a.id);
            }
          });

        p.setSelectedAnnotations(selected);
        return;
      }

      if (
        p.currentTool === 'select' &&
        p.dragStart &&
        p.selectedAnnotations.size > 0 &&
        !p.isSelecting
      ) {
        const dx = x - p.dragStart.x;
        const dy = y - p.dragStart.y;

        p.setAnnotations((prev: Annotation[]) => {
          const movedAnnotationIds = new Set<string>();

          let updated = prev.map((a) => {
            if (p.selectedAnnotations.has(a.id) && a.type !== 'piping') {
              movedAnnotationIds.add(a.id);

              const updatedAnn: Annotation = {
                ...a,
                startX: a.startX + dx,
                startY: a.startY + dy,
                relativeStartX: undefined,
                relativeStartY: undefined,
                x: a.x !== undefined ? a.x + dx : undefined,
                y: a.y !== undefined ? a.y + dy : undefined,
                relativeX: a.x !== undefined ? undefined : a.relativeX,
                relativeY: a.y !== undefined ? undefined : a.relativeY,
              };

              if (a.endX !== undefined) updatedAnn.endX = a.endX + dx;
              if (a.endY !== undefined) updatedAnn.endY = a.endY + dy;

              if (a.type === 'pen' && a.points) {
                updatedAnn.points = a.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
              }

              if (a.type === 'wire-run' && a.points && a.points.length >= 2) {
                const movedPoints = a.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
                p.dirtyWireRunAnnotationIdsRef.current.add(a.id);
                return p.normalizeWireRunGeometry(updatedAnn, movedPoints, canvas.width, canvas.height);
              }

              return updatedAnn;
            }
            return a;
          });

          updated = p.updateConnectedPipes(updated, movedAnnotationIds);
          p.annotationsRef.current = updated;
          return updated;
        });

        p.setDragStart({ x, y });
        return;
      }

      if (p.currentTool === 'select' && !p.dragStart && !p.isSelecting) {
        // Hover discovery should be independent from edit scope so users can
        // still inspect component names while editing a selected component.
        // Suppress hover churn immediately after wheel zoom to keep zoom smooth.
        const suppressHoverUpdates =
          Date.now() - p.lastWheelTimeRef.current < HOVER_SUPPRESS_AFTER_ZOOM_MS;

        if (!suppressHoverUpdates) {
          const hoveredAnno = p.annotations
            .filter((a) => a.page === p.pageNumber)
            .find((a) => {
              if (a.type === 'rectangle' || a.type === 'circle') {
                return p.isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
              }
              if (a.type === 'line') {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.startX,
                  a.startY,
                  a.endX || a.startX,
                  a.endY || a.startY
                );
                return dist < HIT_BOX_PADDING;
              }
              if (a.type === 'pen' && a.points) {
                for (let i = 1; i < a.points.length; i++) {
                  const dist = p.pointToLineDistance(
                    x,
                    y,
                    a.points[i - 1].x,
                    a.points[i - 1].y,
                    a.points[i].x,
                    a.points[i].y
                  );
                  if (dist < HIT_BOX_PADDING) return true;
                }
              }
              if (a.type === 'wire-run' && a.points) {
                for (let i = 1; i < a.points.length; i++) {
                  const dist = p.pointToLineDistance(
                    x,
                    y,
                    a.points[i - 1].x,
                    a.points[i - 1].y,
                    a.points[i].x,
                    a.points[i].y
                  );
                  if (dist < HIT_BOX_PADDING) return true;
                }
              }
              return false;
            });

          const nextHoveredId = hoveredAnno?.id || null;
          const hoveredChanged = nextHoveredId !== p.hoveredAnnotation;
          if (hoveredChanged) {
            p.setHoveredAnnotation(nextHoveredId);
          }

          if (nextHoveredId) {
            const prevHoverPoint = lastHoverPointRef.current;
            const movedEnough =
              hoveredChanged ||
              !prevHoverPoint ||
              Math.hypot(x - prevHoverPoint.x, y - prevHoverPoint.y) >=
                HOVER_UPDATE_MIN_DISTANCE_PX;
            if (movedEnough) {
              lastHoverPointRef.current = { x, y };
              p.setHoveredAnnotationPoint({ x, y });
            }
          } else if (lastHoverPointRef.current) {
            lastHoverPointRef.current = null;
            p.setHoveredAnnotationPoint(null);
          }
        }
      }

      if (p.currentTool === 'drag' && !p.dragStart && !p.isPanning) {
        // Keep hover labels available across components while dragging too.
        // Suppress hover churn immediately after wheel zoom to keep zoom smooth.
        const suppressHoverUpdates =
          Date.now() - p.lastWheelTimeRef.current < HOVER_SUPPRESS_AFTER_ZOOM_MS;

        if (!suppressHoverUpdates) {
          const hoveredAnno = p.annotations
            .filter((a) => a.page === p.pageNumber)
            .find((a) => {
              if (a.type === 'piping') return false;
              if (a.type === 'rectangle' || a.type === 'circle') {
                return p.isPointInAnnotation(a, x, y, HIT_BOX_PADDING);
              }
              if (a.type === 'line') {
                const dist = p.pointToLineDistance(
                  x,
                  y,
                  a.startX,
                  a.startY,
                  a.endX || a.startX,
                  a.endY || a.startY
                );
                return dist < HIT_BOX_PADDING;
              }
              if (a.type === 'pen' && a.points) {
                for (let i = 1; i < a.points.length; i++) {
                  const dist = p.pointToLineDistance(
                    x,
                    y,
                    a.points[i - 1].x,
                    a.points[i - 1].y,
                    a.points[i].x,
                    a.points[i].y
                  );
                  if (dist < HIT_BOX_PADDING) return true;
                }
              }
              if (a.type === 'wire-run' && a.points) {
                for (let i = 1; i < a.points.length; i++) {
                  const dist = p.pointToLineDistance(
                    x,
                    y,
                    a.points[i - 1].x,
                    a.points[i - 1].y,
                    a.points[i].x,
                    a.points[i].y
                  );
                  if (dist < HIT_BOX_PADDING) return true;
                }
              }
              return false;
            });

          const nextHoveredId = hoveredAnno?.id || null;
          const hoveredChanged = nextHoveredId !== p.hoveredAnnotation;
          if (hoveredChanged) {
            p.setHoveredAnnotation(nextHoveredId);
          }

          if (nextHoveredId) {
            const prevHoverPoint = lastHoverPointRef.current;
            const movedEnough =
              hoveredChanged ||
              !prevHoverPoint ||
              Math.hypot(x - prevHoverPoint.x, y - prevHoverPoint.y) >=
                HOVER_UPDATE_MIN_DISTANCE_PX;
            if (movedEnough) {
              lastHoverPointRef.current = { x, y };
              p.setHoveredAnnotationPoint({ x, y });
            }
          } else if (lastHoverPointRef.current) {
            lastHoverPointRef.current = null;
            p.setHoveredAnnotationPoint(null);
          }
        }
      }

      if (p.currentTool === 'drag' && p.dragStart && p.selectedAnnotation) {
        const selectedAnn = p.annotations.find((a) => a.id === p.selectedAnnotation);
        if (selectedAnn && selectedAnn.type === 'piping') {
          return;
        }

        const dx = x - p.dragStart.x;
        const dy = y - p.dragStart.y;

        p.setAnnotations((prev: Annotation[]) => {
          let updated = prev.map((a) => {
            if (a.id === p.selectedAnnotation) {
              const updatedAnn: Annotation = {
                ...a,
                startX: a.startX + dx,
                startY: a.startY + dy,
                endX: a.endX ? a.endX + dx : undefined,
                endY: a.endY ? a.endY + dy : undefined,
                relativeStartX: undefined,
                relativeStartY: undefined,
                x: a.x !== undefined ? a.x + dx : undefined,
                y: a.y !== undefined ? a.y + dy : undefined,
                relativeX: a.x !== undefined ? undefined : a.relativeX,
                relativeY: a.y !== undefined ? undefined : a.relativeY,
              };

              if (a.type === 'pen' && a.points) {
                updatedAnn.points = a.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
              }

              if (a.type === 'wire-run' && a.points && a.points.length >= 2) {
                const movedPoints = a.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
                p.dirtyWireRunAnnotationIdsRef.current.add(a.id);
                return p.normalizeWireRunGeometry(updatedAnn, movedPoints, canvas.width, canvas.height);
              }

              return updatedAnn;
            }
            return a;
          });

          updated = p.updateConnectedPipes(updated, new Set([p.selectedAnnotation!]));
          p.annotationsRef.current = updated;
          return updated;
        });

        p.setDragStart({ x, y });
      } else if (p.isCapturingSnapshot && p.snapshotSelection) {
        p.setSnapshotSelection({
          ...p.snapshotSelection,
          endX: x,
          endY: y,
        });
      } else if (p.isDrawing && p.currentAnnotation) {
        switch (p.currentAnnotation.type) {
          case 'rectangle':
          case 'circle':
            p.setCurrentAnnotation({
              ...p.currentAnnotation,
              width: x - (p.currentAnnotation.startX || 0),
              height: y - (p.currentAnnotation.startY || 0),
            });
            break;
          case 'line':
            p.setCurrentAnnotation({
              ...p.currentAnnotation,
              endX: x,
              endY: y,
            });
            break;
          case 'pen':
            p.setCurrentAnnotation({
              ...p.currentAnnotation,
              points: [...(p.currentAnnotation.points || []), { x, y }],
            });
            break;
        }
      }
    },
    [p]
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (!p.wireRunExtendMode && (p.isRightClickPanning || p.isPanning)) {
        return;
      }

      if (p.wireRunContextMenu) p.setWireRunContextMenu(null);
      if (p.wireRunEditContextMenu) p.setWireRunEditContextMenu(null);

      const { x, y } = p.getScreenToCanvasCoords(e.clientX, e.clientY);

      if (p.wireRunExtendMode) {
        if (p.isRightClickPanning) p.setIsRightClickPanning(false);
        if (p.isPanning) p.setIsPanning(false);
        if (p.panStart) p.setPanStart(null);

        const cw = p.canvasRef.current?.width || 16000;
        const ch = p.canvasRef.current?.height || 10666;
        const targetWireRun = p.annotations
          .filter(
            (a) =>
              a.page === p.pageNumber &&
              a.type === 'wire-run' &&
              a.id === p.wireRunExtendMode!.annotationId
          )
          .map((a) => p.rescaleAnnotation(a, cw, ch))[0];

        if (targetWireRun?.points && targetWireRun.points.length >= 2) {
          const endpointIndex = p.wireRunExtendMode.endpoint === 'start' ? 0 : targetWireRun.points.length - 1;
          const endpoint = targetWireRun.points[endpointIndex];
          const fallbackPoint = e.shiftKey ? p.constrainTo90Degrees(endpoint, { x, y }) : { x, y };
          const contextPoint = p.wireRunExtendPreviewPoint || fallbackPoint;

          if (
            Math.hypot(contextPoint.x - endpoint.x, contextPoint.y - endpoint.y) > 0.0005
          ) {
            const nextPoint = e.shiftKey
              ? p.constrainTo90Degrees(endpoint, contextPoint)
              : contextPoint;
            const nextPoints =
              p.wireRunExtendMode.endpoint === 'start'
                ? [nextPoint, ...targetWireRun.points]
                : [...targetWireRun.points, nextPoint];
            const currentAnnotations = p.annotationsRef.current;
            const nextAnnotations = currentAnnotations.map((annotation) => {
              if (annotation.id !== targetWireRun.id || annotation.type !== 'wire-run') return annotation;
              return p.normalizeWireRunGeometry(annotation, nextPoints, cw, ch);
            });
            p.annotationsRef.current = nextAnnotations;
            p.setAnnotations(nextAnnotations);
            p.addToHistory(nextAnnotations);
            void p.persistAnnotations(nextAnnotations, [targetWireRun.id]);
            void p.syncWireRunsToDatabase([targetWireRun.id], nextAnnotations);
          }
        }

        p.setWireRunExtendMode(null);
        p.setWireRunExtendPreviewPoint(null);
        return;
      }

      if (p.isWireRunMode && p.selectedWireType) {
        console.log('✅ Showing wire run context menu at:', e.clientX, e.clientY);
        p.setWireRunEditContextMenu(null);
        p.setWireRunContextMenu({ x: e.clientX, y: e.clientY });
        return;
      }

      if (p.openWireRunEditContextMenuAtPoint(e.clientX, e.clientY, x, y)) {
        p.setWireRunContextMenu(null);
        return;
      }

      const hitAnnotation = p.findAnnotationAtPoint(x, y);
      if (hitAnnotation) {
        console.log('🗑️ Right-click delete annotation:', hitAnnotation.id);
        const currentAnnotations = p.annotationsRef.current;
        const deletedIds = new Set<string>([hitAnnotation.id]);
        currentAnnotations.forEach((annotation) => {
          if (annotation.type !== 'piping') return;
          const startDeleted =
            annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
          const endDeleted =
            annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
          if (startDeleted || endDeleted) deletedIds.add(annotation.id);
        });
        const filtered = currentAnnotations.filter((annotation) => !deletedIds.has(annotation.id));
        const newAnnotations = p.renumberAnnotations(filtered);
        const deletedComponentTypes = Array.from(
          new Set(
            currentAnnotations
              .filter((annotation) => deletedIds.has(annotation.id))
              .map((annotation) => annotation.component_type)
              .filter(Boolean)
          )
        ) as string[];
        p.annotationsRef.current = newAnnotations;
        p.setAnnotations(newAnnotations);
        p.addToHistory(newAnnotations);
        p.setSelectedAnnotation(null);
        void p.persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
        void p.deletePersistedWireRunsForAnnotationIds(deletedIds);
        return;
      }

      console.log('🖱️ Right-click detected!', {
        activePlacementComponent: p.activePlacementComponent?.name,
        countingModeActive: p.countingModeActive,
        isWireRunMode: p.isWireRunMode,
      });
    },
    [p]
  );

  const handleMouseUp = useCallback(
    async () => {
      if (p.resizeHandle) {
        console.log('🔲 Finished resizing rectangle');
        const resizedAnnotationId = p.resizeHandle.annotationId;
        p.addToHistory(p.annotations);
        setTimeout(() => {
          void p.persistAnnotations(p.annotationsRef.current, [resizedAnnotationId]);
        }, 0);
        p.setResizeHandle(null);
        return;
      }

      if (p.isCalibrating && p.calibrationLine && p.isDrawing) {
        console.log('📏 Calibration: Line complete', {
          percentage: p.calibrationLine.percentage,
          willShowModal: p.calibrationLine.percentage > 0,
        });
        p.setIsDrawing(false);
        if (p.calibrationLine.percentage > 0) {
          console.log('✅ Opening calibration modal for real-world value input');
          p.setShowCalibrationModal(true);
        } else {
          console.log('⚠️ Calibration line too short (0%), not showing modal');
        }
        return;
      }

      if (p.isCapturingSnapshot && p.snapshotSelection) {
        if (p.autoExtractKeyOnSnapshot) {
          p.setIsCapturingSnapshot(false);
          await p.handleExtractKey();
        } else {
          p.setIsCapturingSnapshot(false);
          p.setShowSnapshotActions(true);
        }
      } else if (p.isDrawing && p.currentAnnotation) {
        const completedAnnotation = { ...p.currentAnnotation } as Annotation;

        if (
          p.countingModeActive &&
          p.activePlacementComponent &&
          p.currentTool === 'select' &&
          completedAnnotation.type === 'rectangle'
        ) {
          const clickWidth = Math.abs(completedAnnotation.width || 0);
          const clickHeight = Math.abs(completedAnnotation.height || 0);
          const CLICK_PLACE_THRESHOLD = 2;
          const isPlainClick =
            clickWidth <= CLICK_PLACE_THRESHOLD &&
            clickHeight <= CLICK_PLACE_THRESHOLD;

          if (isPlainClick) {
            const placementComponentType =
              completedAnnotation.component_type ||
              (p.activePlacementComponent.symbolType === 'Lighting'
                ? p.activePlacementComponent.symbol || p.activePlacementComponent.name
                : p.activePlacementComponent.name);

            const existingRects = p.annotationsRef.current
              .filter(
                (annotation) =>
                  annotation.type === 'rectangle' &&
                  annotation.component_type === placementComponentType
              )
              .map((annotation) => ({
                width: Math.abs(annotation.width || 0),
                height: Math.abs(annotation.height || 0),
              }))
              .filter((size) => size.width > 0 && size.height > 0);

            const DEFAULT_CLICK_PLACEMENT_SIZE = 60;
            const averageWidth = existingRects.length
              ? existingRects.reduce((sum, size) => sum + size.width, 0) / existingRects.length
              : DEFAULT_CLICK_PLACEMENT_SIZE;
            const averageHeight = existingRects.length
              ? existingRects.reduce((sum, size) => sum + size.height, 0) / existingRects.length
              : DEFAULT_CLICK_PLACEMENT_SIZE;

            const placementWidth = Math.max(8, averageWidth);
            const placementHeight = Math.max(8, averageHeight);
            const clickX = completedAnnotation.startX || 0;
            const clickY = completedAnnotation.startY || 0;

            completedAnnotation.startX = clickX - placementWidth / 2;
            completedAnnotation.startY = clickY - placementHeight / 2;
            completedAnnotation.width = placementWidth;
            completedAnnotation.height = placementHeight;
            completedAnnotation.component_type = placementComponentType;
          }
        }

        if (completedAnnotation.type === 'rectangle') {
          const width = completedAnnotation.width || 0;
          const height = completedAnnotation.height || 0;

          if (width < 0) {
            completedAnnotation.startX = (completedAnnotation.startX || 0) + width;
            completedAnnotation.width = Math.abs(width);
          }
          if (height < 0) {
            completedAnnotation.startY = (completedAnnotation.startY || 0) + height;
            completedAnnotation.height = Math.abs(height);
          }

          const minSize = 0;
          if ((completedAnnotation.width || 0) < minSize) {
            completedAnnotation.width = minSize;
          }
          if ((completedAnnotation.height || 0) < minSize) {
            completedAnnotation.height = minSize;
          }
        }

        const baseAnnotations = p.annotationsRef.current;

        if (
          completedAnnotation.type === 'circle' ||
          completedAnnotation.type === 'rectangle'
        ) {
          const componentType = completedAnnotation.component_type || 'annotation';
          const existingCount = baseAnnotations.filter(
            (a) => a.component_type === componentType && a.page === p.pageNumber
          ).length;
          completedAnnotation.number = existingCount + 1;

          if (!completedAnnotation.component_type) {
            completedAnnotation.component_type = 'annotation';
          }
        }

        void p.persistCreatedAnnotations([completedAnnotation])
          .then(({ next }) => {
            p.addToHistory(next);
          })
          .catch((persistError) => {
            console.error('Error persisting created annotation:', persistError);
          });

        if (
          completedAnnotation.component_type &&
          p.countingModeActive &&
          p.activePlacementComponent
        ) {
          p.setSelectedAnnotation(completedAnnotation.id);

          const lastPlaced = [...p.lastPlacedAnnotationsRef.current, completedAnnotation.id].slice(
            -20
          );
          p.lastPlacedAnnotationsRef.current = lastPlaced;

          console.log(
            `📍 Placed ${completedAnnotation.component_type} rectangle at (${Math.round(completedAnnotation.startX || 0)}, ${Math.round(completedAnnotation.startY || 0)}) size ${Math.round(completedAnnotation.width || 0)}x${Math.round(completedAnnotation.height || 0)}`
          );

          console.log('💾 Immediate save triggered for counting mode');
        }
      }

      if (
        p.autocountMode === 'area-selection' &&
        p.autocountSelection
      ) {
        let width = Math.abs(p.autocountSelection.endX - p.autocountSelection.startX);
        let height = Math.abs(p.autocountSelection.endY - p.autocountSelection.startY);

        const MIN_SIZE = 50;

        let selectionToUse = p.autocountSelection;
        if (width < MIN_SIZE || height < MIN_SIZE) {
          console.log(
            `📏 Expanding small selection from ${width}x${height} to minimum ${MIN_SIZE}x${MIN_SIZE}`
          );

          const centerX = (p.autocountSelection.startX + p.autocountSelection.endX) / 2;
          const centerY = (p.autocountSelection.startY + p.autocountSelection.endY) / 2;

          const expandedWidth = Math.max(width, MIN_SIZE);
          const expandedHeight = Math.max(height, MIN_SIZE);

          selectionToUse = {
            startX: centerX - expandedWidth / 2,
            startY: centerY - expandedHeight / 2,
            endX: centerX + expandedWidth / 2,
            endY: centerY + expandedHeight / 2,
          };

          p.setAutocountSelection(selectionToUse);

          width = expandedWidth;
          height = expandedHeight;
        }

        console.log('✅ Autocount area selected:', { width, height });

        const annotationCanvas = p.canvasRef.current;
        const pdfCanvas = document.querySelector(
          '.react-pdf__Page__canvas'
        ) as HTMLCanvasElement;

        if (annotationCanvas && pdfCanvas) {
          const x = Math.min(selectionToUse.startX, selectionToUse.endX);
          const y = Math.min(selectionToUse.startY, selectionToUse.endY);

          console.log('📸 Capturing reference area:', { x, y, width, height });

          p.drawAnnotations(true);

          const combinedCanvas = document.createElement('canvas');
          combinedCanvas.width = annotationCanvas.width;
          combinedCanvas.height = annotationCanvas.height;
          const combinedCtx = combinedCanvas.getContext('2d');

          if (combinedCtx) {
            combinedCtx.drawImage(
              pdfCanvas,
              0,
              0,
              annotationCanvas.width,
              annotationCanvas.height
            );
            combinedCtx.drawImage(annotationCanvas, 0, 0);

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');

            if (tempCtx) {
              tempCtx.drawImage(
                combinedCanvas,
                x,
                y,
                width,
                height,
                0,
                0,
                width,
                height
              );
              const referenceImage = tempCanvas.toDataURL('image/png');
              p.setReferenceAreaImage(referenceImage);

              p.setEditingQueuedName(p.pendingAutocountComponent?.name || '');

              p.setAutocountMode(null);
              p.setAutocountSelection(null);

              if (p.isAddingNewOnlyScanMode) {
                p.setShowNewComponentModal(true);
              } else if (p.isAddingNewOnly) {
                p.setShowNewComponentModal(true);
              } else {
                p.setShowAutocountConfirmModal(true);
              }

              console.log('📸 Reference area image extracted from combined canvas');
            }
          }

          p.drawAnnotations();
        }

        return;
      }

      if (p.keyExtractionMode && p.keyExtractionSelection) {
        let width = Math.abs(p.keyExtractionSelection.endX - p.keyExtractionSelection.startX);
        let height = Math.abs(p.keyExtractionSelection.endY - p.keyExtractionSelection.startY);

        const MIN_SIZE = 50;

        if (width < MIN_SIZE || height < MIN_SIZE) {
          console.log('❌ Key extraction selection too small, canceling');
          p.setKeyExtractionSelection(null);
          return;
        }

        console.log('✅ Key extraction area selected:', { width, height });

        const annotationCanvas = p.canvasRef.current;
        const pdfCanvas = document.querySelector(
          '.react-pdf__Page__canvas'
        ) as HTMLCanvasElement;

        if (annotationCanvas && pdfCanvas) {
          const x = Math.min(
            p.keyExtractionSelection.startX,
            p.keyExtractionSelection.endX
          );
          const y = Math.min(
            p.keyExtractionSelection.startY,
            p.keyExtractionSelection.endY
          );

          console.log('📸 Capturing key area:', { x, y, width, height });

          p.drawAnnotations(true);

          const combinedCanvas = document.createElement('canvas');
          combinedCanvas.width = annotationCanvas.width;
          combinedCanvas.height = annotationCanvas.height;
          const combinedCtx = combinedCanvas.getContext('2d');

          if (combinedCtx) {
            combinedCtx.drawImage(
              pdfCanvas,
              0,
              0,
              annotationCanvas.width,
              annotationCanvas.height
            );
            combinedCtx.drawImage(annotationCanvas, 0, 0);

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');

            if (tempCtx) {
              tempCtx.drawImage(
                combinedCanvas,
                x,
                y,
                width,
                height,
                0,
                0,
                width,
                height
              );
              const keyImage = tempCanvas.toDataURL('image/png');

              p.setKeyExtractionSnapshot(keyImage);
              p.setShowKeyExtractionConfirmModal(true);

              console.log('📸 Key area image captured, showing confirmation modal');
            }
          }

          p.drawAnnotations();
        }

        return;
      }

      if (p.lightingScheduleMode && p.lightingScheduleSelection) {
        let width = Math.abs(
          p.lightingScheduleSelection.endX - p.lightingScheduleSelection.startX
        );
        let height = Math.abs(
          p.lightingScheduleSelection.endY - p.lightingScheduleSelection.startY
        );

        const MIN_SIZE = 50;

        if (width < MIN_SIZE || height < MIN_SIZE) {
          console.log('❌ Lighting schedule selection too small, canceling');
          p.setLightingScheduleSelection(null);
          return;
        }

        console.log('✅ Lighting schedule area selected:', { width, height });

        const annotationCanvas = p.canvasRef.current;
        const pdfCanvas = document.querySelector(
          '.react-pdf__Page__canvas'
        ) as HTMLCanvasElement;

        if (annotationCanvas && pdfCanvas) {
          const x = Math.min(
            p.lightingScheduleSelection.startX,
            p.lightingScheduleSelection.endX
          );
          const y = Math.min(
            p.lightingScheduleSelection.startY,
            p.lightingScheduleSelection.endY
          );

          console.log('📸 Capturing lighting schedule area:', { x, y, width, height });

          p.drawAnnotations(true);

          const combinedCanvas = document.createElement('canvas');
          combinedCanvas.width = annotationCanvas.width;
          combinedCanvas.height = annotationCanvas.height;
          const combinedCtx = combinedCanvas.getContext('2d');

          if (combinedCtx) {
            combinedCtx.drawImage(
              pdfCanvas,
              0,
              0,
              annotationCanvas.width,
              annotationCanvas.height
            );
            combinedCtx.drawImage(annotationCanvas, 0, 0);

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');

            if (tempCtx) {
              tempCtx.drawImage(
                combinedCanvas,
                x,
                y,
                width,
                height,
                0,
                0,
                width,
                height
              );
              const scheduleImage = tempCanvas.toDataURL('image/png');

              p.setLightingScheduleSnapshot(scheduleImage);
              p.setShowLightingScheduleConfirmModal(true);

              console.log('📸 Lighting schedule image captured, showing confirmation modal');
            }
          }

          p.drawAnnotations();
        }

        return;
      }

      if (
        (p.dragStart && p.selectedAnnotations.size > 0 && p.currentTool === 'select') ||
        (p.currentTool === 'select' && p.wireRunVertexDrag)
      ) {
        p.addToHistory(p.annotations);
      }

      const movedAnnotationIdsForPersist = new Set<string>();
      if (!p.currentAnnotation && p.dragStart && !p.isSelecting) {
        if (p.currentTool === 'select' && p.selectedAnnotations.size > 0) {
          const selectedIds = Array.from(p.selectedAnnotations);
          for (const id of selectedIds) {
            const annotation = p.annotationsRef.current.find((row) => row.id === id);
            if (!annotation) continue;
            if (annotation.type === 'piping' || annotation.type === 'wire-run') continue;
            movedAnnotationIdsForPersist.add(id);
          }
        }

        if (p.currentTool === 'drag' && p.selectedAnnotation) {
          const annotation = p.annotationsRef.current.find((row) => row.id === p.selectedAnnotation);
          if (annotation && annotation.type !== 'piping' && annotation.type !== 'wire-run') {
            movedAnnotationIdsForPersist.add(annotation.id);
          }
        }

        if (movedAnnotationIdsForPersist.size > 0) {
          for (const annotation of p.annotationsRef.current) {
            if (annotation.type !== 'piping') continue;
            const startMoved = annotation.startAnnotationId && movedAnnotationIdsForPersist.has(annotation.startAnnotationId);
            const endMoved = annotation.endAnnotationId && movedAnnotationIdsForPersist.has(annotation.endAnnotationId);
            if (startMoved || endMoved) {
              movedAnnotationIdsForPersist.add(annotation.id);
            }
          }
        }
      }

      if (p.isSelecting && p.selectionRect) {
        p.setIsSelecting(false);

        const minX = Math.min(p.selectionRect.startX, p.selectionRect.endX);
        const maxX = Math.max(p.selectionRect.startX, p.selectionRect.endX);
        const minY = Math.min(p.selectionRect.startY, p.selectionRect.endY);
        const maxY = Math.max(p.selectionRect.startY, p.selectionRect.endY);

        const selectedIds = new Set<string>();

        p.annotations
          .filter((a) => a.page === p.pageNumber)
          .filter((a) => {
            if (a.type === 'wire-run') return true;
            if (!p.highlightedComponentType) return false;
            if (p.isHighlightAll) return true;
            if (a.type === 'piping') {
              const startAnn = p.annotations.find((ann) => ann.id === a.startAnnotationId);
              const endAnn = p.annotations.find((ann) => ann.id === a.endAnnotationId);
              return (
                startAnn?.component_type === p.highlightedComponentType ||
                endAnn?.component_type === p.highlightedComponentType
              );
            }
            return a.component_type === p.highlightedComponentType;
          })
          .forEach((annotation) => {
            let isInRect = false;

            if (annotation.type === 'rectangle' || annotation.type === 'circle') {
              const annMinX = Math.min(
                annotation.startX,
                annotation.startX + (annotation.width || 0)
              );
              const annMaxX = Math.max(
                annotation.startX,
                annotation.startX + (annotation.width || 0)
              );
              const annMinY = Math.min(
                annotation.startY,
                annotation.startY + (annotation.height || 0)
              );
              const annMaxY = Math.max(
                annotation.startY,
                annotation.startY + (annotation.height || 0)
              );
              isInRect = !(annMaxX < minX || annMinX > maxX || annMaxY < minY || annMinY > maxY);
            } else if (annotation.type === 'line') {
              const startInRect =
                annotation.startX >= minX &&
                annotation.startX <= maxX &&
                annotation.startY >= minY &&
                annotation.startY <= maxY;
              const endInRect =
                (annotation.endX || annotation.startX) >= minX &&
                (annotation.endX || annotation.startX) <= maxX &&
                (annotation.endY || annotation.startY) >= minY &&
                (annotation.endY || annotation.startY) <= maxY;
              isInRect = startInRect || endInRect;
            } else if (annotation.type === 'wire-run' && annotation.points) {
              isInRect = annotation.points.some(
                (point: { x: number; y: number }) =>
                  point.x >= minX &&
                  point.x <= maxX &&
                  point.y >= minY &&
                  point.y <= maxY
              );
            } else if (annotation.x !== undefined && annotation.y !== undefined) {
              isInRect =
                annotation.x >= minX &&
                annotation.x <= maxX &&
                annotation.y >= minY &&
                annotation.y <= maxY;
            }

            if (isInRect) {
              selectedIds.add(annotation.id);
            }
          });

        p.setSelectedAnnotations(selectedIds);

        if (selectedIds.size === 1) {
          p.setSelectedAnnotation(Array.from(selectedIds)[0]);
        } else if (selectedIds.size === 0) {
          p.setSelectedAnnotation(null);
        } else {
          p.setSelectedAnnotation(null);
        }

        console.log(`📦 Selected ${selectedIds.size} annotations with rectangle`);
      }

      p.setIsDrawing(false);
      p.setCurrentAnnotation(null);
      p.setDragStart(null);
      p.setWireRunVertexDrag(null);

      if (p.isRightClickPanning) {
        console.log('🖱️ Right-click pan ended');
        p.setIsRightClickPanning(false);
      }

      p.setIsPanning(false);
      p.setPanStart(null);
      p.setSelectionRect(null);

      const dirtyWireRunIds = Array.from(p.dirtyWireRunAnnotationIdsRef.current);
      if (dirtyWireRunIds.length > 0) {
        p.dirtyWireRunAnnotationIdsRef.current.clear();
        void p.syncWireRunsToDatabase(dirtyWireRunIds, p.annotationsRef.current);
        void p.persistAnnotations(p.annotationsRef.current, dirtyWireRunIds);
      }

      if (movedAnnotationIdsForPersist.size > 0) {
        void p.persistAnnotations(
          p.annotationsRef.current,
          Array.from(movedAnnotationIdsForPersist)
        );
      }
    },
    [p]
  );

  return {
    handleMouseDown,
    handleMouseMove,
    handleContextMenu,
    handleMouseUp,
  };
}
